import React, { useMemo } from "react";
import { Stack, Text } from "@fluentui/react";
import type { IntegrationUsageSummaryDto } from "@/api/integrations";

interface Props {
  data: IntegrationUsageSummaryDto[];
  getIntegrationName: (id: number) => string;
}

interface AggregatedBar {
  integrationId: number;
  label: string;
  totalUnits: number;
  estimatedCost: number;
  currencyCode: string | null;
}

const COLORS = [
  "#0078d4", "#00b7c3", "#8764b8", "#e3008c",
  "#107c10", "#ca5010", "#4f6bed", "#da3b01",
];

const UsageChart = ({ data, getIntegrationName }: Props) => {
  const bars = useMemo<AggregatedBar[]>(() => {
    const map = new Map<number, AggregatedBar>();
    for (const row of data) {
      const existing = map.get(row.integrationId);
      if (existing) {
        existing.totalUnits += row.totalUnits;
        existing.estimatedCost += row.estimatedCost ?? 0;
        if (!existing.currencyCode && row.currencyCode)
          existing.currencyCode = row.currencyCode;
      } else {
        map.set(row.integrationId, {
          integrationId: row.integrationId,
          label: getIntegrationName(row.integrationId),
          totalUnits: row.totalUnits,
          estimatedCost: row.estimatedCost ?? 0,
          currencyCode: row.currencyCode,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.totalUnits - a.totalUnits);
  }, [data, getIntegrationName]);

  const maxUnits = Math.max(...bars.map((b) => b.totalUnits), 1);

  if (bars.length === 0) {
    return (
      <Stack horizontalAlign="center" styles={{ root: { padding: 40 } }}>
        <Text variant="medium" styles={{ root: { color: "#666" } }}>
          No usage data for the selected period.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { padding: "8px 0" } }}>
      {bars.map((bar, idx) => {
        const pct = (bar.totalUnits / maxUnits) * 100;
        const color = COLORS[idx % COLORS.length];
        const costLabel =
          bar.estimatedCost > 0
            ? `${bar.currencyCode ?? ""} ${bar.estimatedCost.toFixed(2)}`
            : null;

        return (
          <Stack
            key={bar.integrationId}
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { minHeight: 36 } }}
          >
            <Text
              styles={{
                root: {
                  width: 140,
                  textAlign: "right",
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
              title={bar.label}
            >
              {bar.label}
            </Text>
            <Stack
              styles={{
                root: {
                  flex: 1,
                  position: "relative",
                  height: 28,
                  backgroundColor: "#f3f2f1",
                  borderRadius: 4,
                  overflow: "hidden",
                },
              }}
            >
              <div
                style={{
                  width: `${Math.max(pct, 1)}%`,
                  height: "100%",
                  backgroundColor: color,
                  borderRadius: 4,
                  transition: "width 0.3s ease",
                }}
              />
            </Stack>
            <Stack
              horizontal
              tokens={{ childrenGap: 12 }}
              styles={{ root: { minWidth: 180, justifyContent: "flex-end" } }}
            >
              <Text styles={{ root: { fontSize: 13, fontWeight: 600 } }}>
                {bar.totalUnits.toLocaleString()} units
              </Text>
              {costLabel && (
                <Text styles={{ root: { fontSize: 13, color: "#605e5c" } }}>
                  {costLabel}
                </Text>
              )}
            </Stack>
          </Stack>
        );
      })}

      <Stack
        horizontal
        horizontalAlign="end"
        tokens={{ childrenGap: 24 }}
        styles={{
          root: {
            borderTop: "1px solid #edebe9",
            marginTop: 12,
            paddingTop: 12,
          },
        }}
      >
        <Text styles={{ root: { fontSize: 14, fontWeight: 600 } }}>
          Total Units: {bars.reduce((s, b) => s + b.totalUnits, 0).toLocaleString()}
        </Text>
        {(() => {
          const totalCost = bars.reduce((s, b) => s + b.estimatedCost, 0);
          const currency = bars.find((b) => b.currencyCode)?.currencyCode;
          return totalCost > 0 ? (
            <Text styles={{ root: { fontSize: 14, fontWeight: 600 } }}>
              Total Cost: {currency ?? ""} {totalCost.toFixed(2)}
            </Text>
          ) : null;
        })()}
      </Stack>
    </Stack>
  );
};

export default UsageChart;
